import axios from "axios";
import {
    GET_USERS_BY_ROLE_URL,
    ASSIGN_STATUS_REVIEWERS,
    GET_STATUS_REVIEWERS,
    UNASSIGN_STATUS_REVIEWERS,
    REVIEWER_TYPE_USER,
    REVIEWER_TYPE_ROLE,
    ASSIGNMENT_TYPE_ROLE
} from "@/utils/constants/constants.js";

export const status_reviewers_mixin = {
    data: () => ({
        reviewer_count: null,
        reviewers: [],
        statuses: [],
        form_statuses: [],
    }),

    methods: {
        initialize_status_reviewer: () => ({
            assignment_type: null,
            assigned_users: [],
            selected_role: null,
            role_name: null,
            available_users: [],
            selected_users: [],
            user_search: "",
            filtered_users: [],
            type: null,
            is_existing: false
        }),

        update_status_reviewers(count, status_index) {
            const status = Reflect.get(this.workflow_statuses, status_index);

            if (count < 0) {
                Reflect.set(status, 'status_reviewer_count', 0);
                return;
            }

            if (!Reflect.get(status, 'status_reviewers')) {
                Reflect.set(status, 'status_reviewers', []);
            }

            const status_reviewers = Reflect.get(status, 'status_reviewers');

            if (count > status_reviewers.length) {
                const reviewers_to_add = count - status_reviewers.length;
                for (let i = 0; i < reviewers_to_add; i++) {
                    status_reviewers.push(this.initialize_status_reviewer());
                }
            } else if (count < status_reviewers.length) {
                const empty_reviewers = status_reviewers.filter(reviewer =>
                    (!reviewer.assigned_users || reviewer.assigned_users.length === 0) && !reviewer.assigned_role
                );

                const assigned_reviewers = status_reviewers.filter(reviewer =>
                    (reviewer.assigned_users && reviewer.assigned_users.length > 0) || reviewer.assigned_role
                );

                const empty_to_remove = Math.min(empty_reviewers.length, status_reviewers.length - count);

                if (empty_to_remove > 0) {
                    for (let i = 0; i < empty_to_remove; i++) {
                        const empty_index = status_reviewers.findIndex(reviewer =>
                            (!reviewer.assigned_users || reviewer.assigned_users.length === 0) && !reviewer.assigned_role
                        );
                        if (empty_index !== -1) {
                            status_reviewers.splice(empty_index, 1);
                        }
                    }
                }

                Reflect.set(status, 'status_reviewer_count', assigned_reviewers.length + empty_reviewers.length - empty_to_remove);
            }
        },

        remove_status_reviewer(status, reviewer_index) {
            const status_reviewers = Reflect.get(status, 'status_reviewers');
            if (status_reviewers && status_reviewers.length > 1) {
                status_reviewers.splice(reviewer_index, 1);
                Reflect.set(status, 'status_reviewer_count', status_reviewers.length);
            }
        },

        async get_users_by_role_for_status_reviewer(status, reviewer_index) {
            const status_reviewers = Reflect.get(status, 'status_reviewers');
            const reviewer = Reflect.get(status_reviewers, reviewer_index);

            if (!Reflect.get(reviewer, "selected_role")) {
                Reflect.set(reviewer, "available_users", []);
                Reflect.set(reviewer, "filtered_users", []);
                Reflect.set(reviewer, "user_search", "");
                return;
            }

            const selected_role = Reflect.get(reviewer, "selected_role");
            const is_valid_role_id = /^[a-zA-Z0-9_-]+$/.test(String(selected_role));
            if (!is_valid_role_id) {
                this.show_error("Invalid role ID");
                return;
            }

            try {
                const response = await axios.post(
                    GET_USERS_BY_ROLE_URL,
                    {},
                    {
                        headers: this.headers,
                        params: { role_id: selected_role },
                    }
                );

                if (response.data.status === "success") {
                    Reflect.set(reviewer, "available_users", response.data.data);
                    Reflect.set(reviewer, "filtered_users", [...response.data.data]);
                    Reflect.set(reviewer, "user_search", "");
                } else {
                    this.show_error("Failed to fetch users for this role");
                }
            } catch (error) {
                this.show_error("Error fetching users: " + error.message);
            }
        },

        async save_status_reviewer_users(status, reviewer_index) {
            const status_reviewers = Reflect.get(status, 'status_reviewers');
            const reviewer = Reflect.get(status_reviewers, reviewer_index);

            if (Reflect.get(reviewer, 'is_existing')) {
                this.show_error("This is an existing assignment. Use update instead.");
                return;
            }

            if (Reflect.get(reviewer, 'assignment_type') === ASSIGNMENT_TYPE_ROLE) {
                await this.handle_role_assignment(status, reviewer);
            } else {
                await this.handle_user_assignment(status, reviewer);
            }
        },

        async handle_role_assignment(status, reviewer) {
            if (!Reflect.get(reviewer, "selected_role")) {
                this.show_error("Please select a role");
                return;
            }

            const payload = this.build_role_assignment_payload(status, reviewer);
            await this.execute_reviewer_assignment(status, payload, "role");
        },

        async handle_user_assignment(status, reviewer) {
            const selected_users = Reflect.get(reviewer, "selected_users");
            if (!selected_users || selected_users.length === 0) {
                this.show_error("Please select at least one user");
                return;
            }

            const payload = this.build_user_assignment_payload(status, reviewer);
            await this.execute_reviewer_assignment(status, payload, "user");
        },

        build_role_assignment_payload(status, reviewer) {
            const assignment_payload = {
                form_id: this.form_id,
                status_id: Reflect.get(status, 'id'),
                required_reviewer_count: Reflect.get(reviewer, 'required_reviewer_count') || 1,
                reviewers: [
                    {
                        role_id: parseInt(Reflect.get(reviewer, "selected_role"), 10),
                    },
                ],
                assigner_id: this.current_user_id,
                incremental: true,
            }
            return assignment_payload;
        },

        build_user_assignment_payload(status, reviewer) {
            const selected_users = Reflect.get(reviewer, "selected_users");
            const reviewers = selected_users.map((user_id) => ({
                user_id: user_id,
            }));

            const payload = {
                form_id: this.form_id,
                status_id: Reflect.get(status, 'id'),
                required_reviewer_count:
                    Reflect.get(reviewer, 'required_reviewer_count') || reviewers.length,
                reviewers: reviewers,
                assigner_id: this.current_user_id,
                incremental: true,
            };

            return payload;
        },

        async execute_reviewer_assignment(status, payload, type) {
            const response = await axios.post(ASSIGN_STATUS_REVIEWERS, payload, {
                headers: this.headers,
            });

            if (response.data.status === "success") {
                const message =
                    type === ASSIGNMENT_TYPE_ROLE
                        ? "Role assigned successfully"
                        : "Reviewers assigned successfully";
                this.show_success(message);
                await this.load_status_reviewers(status);
            } else {
                this.show_error(`Failed to assign ${type}: ` + response.data.message);
            }
        },

        async load_status_reviewers(status) {
            const response = await axios.get(GET_STATUS_REVIEWERS, {
                params: { form_id: this.form_id, status_id: Reflect.get(status, 'id') },
                headers: this.headers,
            });

            if (!this.is_successful_response(response)) return;

            const reviewer_data = this.extract_reviewer_data(response);
            await this.process_reviewer_data(status, reviewer_data);
        },

        is_successful_response(response) {
            return Reflect.get(Reflect.get(response, 'data'), 'status') === "success";
        },

        extract_reviewer_data(response) {
            const reviewer_data = Reflect.get(Reflect.get(response, 'data'), 'data');
            return Array.isArray(reviewer_data) ? reviewer_data : [reviewer_data];
        },

        async process_reviewer_data(status, reviewers_list) {
            if (reviewers_list.length === 0) return;

            const data = Reflect.get(reviewers_list, 0);
            const users = this.extract_users(data);
            const roles = this.extract_roles(data);

            this.update_status_reviewer_count(status, data, users, roles);
            this.initialize_status_reviewers(status, users, roles);
            this.update_existing_reviewers(status, users);
            this.add_empty_reviewer_slots(status, users, roles);
        },

        extract_users(data) {
            return Array.isArray(Reflect.get(data, 'users'))
                ? Reflect.get(data, 'users')
                : [];
        },

        extract_roles(data) {
            return Array.isArray(Reflect.get(data, 'roles'))
                ? Reflect.get(data, 'roles')
                : [];
        },

        update_status_reviewer_count(status, data, users, roles) {
            const total_existing = users.length + roles.length;
            const required_reviewer_count = Reflect.get(data, 'required_reviewer_count');

            Reflect.set(status, 'status_reviewer_count', Number.isInteger(required_reviewer_count)
                ? required_reviewer_count
                : total_existing);
        },

        initialize_status_reviewers(status, users, roles) {
            Reflect.set(status, 'status_reviewers', []);
            this.add_user_reviewers(status, users);
            this.add_role_reviewers(status, roles);
        },

        add_user_reviewers(status, users) {
            const status_reviewers = Reflect.get(status, 'status_reviewers');

            users.forEach((loaded_user) => {
                const user_obj = Reflect.get(loaded_user, 'user');
                if (user_obj && typeof user_obj === "object") {
                    status_reviewers.push(
                        this.create_user_reviewer(loaded_user, user_obj)
                    );
                }
            });
        },

        add_role_reviewers(status, roles) {
            const status_reviewers = Reflect.get(status, 'status_reviewers');

            roles.forEach((loaded_role) => {
                const role_id = Reflect.get(loaded_role, 'role_id');
                const role_name = Reflect.get(loaded_role, 'role_name');
                if (role_id && role_name) {
                    status_reviewers.push(
                        this.create_role_reviewer(loaded_role, role_id, role_name)
                    );
                }
            });
        },

        create_user_reviewer(loaded_user, user_obj) {
            const user_reviewer = {
                type: REVIEWER_TYPE_USER,
                id: Reflect.get(loaded_user, 'id') ?? null,
                workflow_reviewer_id: Reflect.get(loaded_user, 'workflow_reviewer_id') ?? null,
                assigned_users: [{
                    id: Reflect.get(user_obj, 'id') ?? null,
                    first_name: Reflect.get(user_obj, 'first_name') ?? "",
                    last_name: Reflect.get(user_obj, 'last_name') ?? "",
                    email: Reflect.get(user_obj, 'email') ?? "",
                    is_active: Reflect.get(user_obj, 'is_active') ?? false,
                    role_id: Reflect.get(user_obj, 'role_id') ?? null,
                    role_name: Reflect.get(user_obj, 'role_name') ?? ""
                }],
                available_users: [],
                filtered_users: [],
                selected_users: Reflect.get(user_obj, 'id') ? [Reflect.get(user_obj, 'id')] : [],
                user_search: '',
                required_reviewer_count: 1,
                is_existing: true
            };
            return user_reviewer;
        },

        create_role_reviewer(loaded_role, role_id, role_name) {
            const role_reviewer = {
                type: REVIEWER_TYPE_ROLE,
                id: Reflect.get(loaded_role, 'id') ?? null,
                workflow_reviewer_id: Reflect.get(loaded_role, 'id') ?? null,
                assigned_role: {
                    id: role_id,
                    name: role_name,
                    date_created: Reflect.get(loaded_role, 'date_created') ?? null
                },
                selected_role: role_id,
                available_users: [],
                filtered_users: [],
                selected_users: [],
                user_search: '',
                required_reviewer_count: 1,
                is_existing: true
            };
            return role_reviewer;
        },

        update_existing_reviewers(status, users) {
            const reviewers = Reflect.get(status, 'reviewers');
            if (Array.isArray(reviewers) && reviewers.length > 0) {
                reviewers.forEach((reviewer, index) => {
                    if (index < users.length) {
                        const user_assignment = Reflect.get(users, index);
                        if (user_assignment && Reflect.get(user_assignment, 'workflow_reviewer_id')) {
                            Reflect.set(reviewer, 'workflow_reviewer_id', Reflect.get(user_assignment, 'workflow_reviewer_id'));
                        }
                    }
                });
            }
        },

        add_empty_reviewer_slots(status, users, roles) {
            const status_reviewers = Reflect.get(status, 'status_reviewers');
            const total_existing = users.length + roles.length;
            const status_reviewer_count = Reflect.get(status, 'status_reviewer_count');

            if (total_existing < status_reviewer_count) {
                const empty_slots_needed = status_reviewer_count - total_existing;
                for (let i = 0; i < empty_slots_needed; i++) {
                    status_reviewers.push(this.create_empty_reviewer());
                }
            }
        },

        create_empty_reviewer() {
            const empty_reviewer = {
                type: null,
                assignment_type: null,
                assigned_users: [],
                assigned_role: null,
                available_users: [],
                filtered_users: [],
                selected_users: [],
                selected_role: null,
                user_search: "",
                required_reviewer_count: 1,
                is_existing: false,
            };
            return empty_reviewer;
        },

        async unassign_status_reviewer_user(user_id, status_id, reviewer_index) {
            const form_id = sessionStorage.getItem("form_id");
            const response = await axios.post(
                UNASSIGN_STATUS_REVIEWERS,
                {
                    form_id: form_id,
                    status_id: status_id,
                    user_id: user_id,
                },
                {
                    headers: this.headers,
                }
            );

            if (response.data.status === "success") {
                this.show_success("Reviewer unassigned successfully");
                const status = this.workflow_statuses.find(
                    (s) => Reflect.get(s, 'id') === status_id
                );

                if (status && Array.isArray(Reflect.get(status, 'status_reviewers'))) {
                    const status_reviewers = Reflect.get(status, 'status_reviewers');
                    const reviewer = Reflect.get(status_reviewers, reviewer_index);

                    if (reviewer) {
                        const assigned_users = Array.isArray(Reflect.get(reviewer, 'assigned_users'))
                            ? Reflect.get(reviewer, 'assigned_users')
                            : [];

                        const filtered_assigned_users = assigned_users.filter(
                            (user) => Reflect.get(user, 'id') !== user_id
                        );

                        Reflect.set(reviewer, 'assigned_users', filtered_assigned_users);

                        if (filtered_assigned_users.length === 0) {
                            status_reviewers.splice(reviewer_index, 1);
                        }
                    }
                }
            } else {
                this.show_error(
                    "Failed to unassign reviewer: " +
                    response.data.message
                );
            }
        },

        confirm_unassign_status_reviewer_role(role_id, status_id, reviewer_index) {
        this.show_confirm(
            "Unassign Role Reviewer",
            "Are you sure you want to unassign this role reviewer? This action cannot be undone.",
            () => {
            this.unassign_status_reviewer_role(role_id, status_id, reviewer_index);
            }
        );
        },

        confirm_unassign_status_reviewer_user(user_id, status_id, reviewer_index) {
        this.show_confirm(
            "Unassign User Reviewer",
            "Are you sure you want to unassign this user reviewer? This action cannot be undone.",
            () => {
            this.unassign_status_reviewer_user(user_id, status_id, reviewer_index);
            }
        );
        },

        async unassign_status_reviewer_role(role_id, status_id, reviewer_index) {
            const form_id = sessionStorage.getItem("form_id");
            const response = await axios.post(
                UNASSIGN_STATUS_REVIEWERS,
                {
                    form_id: form_id,
                    status_id: status_id,
                    role_id: role_id,
                },
                {
                    headers: this.headers,
                }
            );

            if (response.data.status === "success") {
                this.show_success("Role unassigned successfully");
                const status = this.workflow_statuses.find((s) => Reflect.get(s, 'id') === status_id);
                const status_reviewers = Reflect.get(status, 'status_reviewers');

                if (status && status_reviewers) {
                    status_reviewers.splice(reviewer_index, 1);
                }
            } else {
                this.show_error("Failed to unassign role: " + response.data.message);
            }
        },
    },
};